import { Card } from "@/components/ui/card";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card className="p-8">
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <div className="space-y-6">
            <section>
              <h2 className="text-2xl font-semibold mb-3">1. Introduction</h2>
              <p className="text-muted-foreground">
                NextWave AI ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. 
                This Privacy Policy explains how we collect, use, process, and disclose your information when you use our AI-powered 
                analytics platform at app.nextwaveai.space.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">2. Information We Collect</h2>
              <div className="space-y-3">
                <h3 className="text-lg font-medium">2.1 Personal Information</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Email address and authentication credentials</li>
                  <li>Company name and business information</li>
                  <li>Profile information you provide</li>
                  <li>Communication preferences</li>
                </ul>
                
                <h3 className="text-lg font-medium">2.2 Data You Upload</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Business data files (CSV, Excel, JSON, etc.)</li>
                  <li>CRM integration data (when you connect external systems)</li>
                  <li>Dashboard configurations and preferences</li>
                  <li>Workflow automation settings</li>
                </ul>

                <h3 className="text-lg font-medium">2.3 Usage Information</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Log files and usage analytics</li>
                  <li>Feature interactions and preferences</li>
                  <li>Performance metrics and error reports</li>
                  <li>Browser information and IP addresses</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">3. How We Use Your Information</h2>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Provide and improve our AI analytics services</li>
                <li>Generate insights and recommendations from your data</li>
                <li>Maintain and secure your account</li>
                <li>Send service notifications and updates</li>
                <li>Provide customer support</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">4. Data Storage and Security</h2>
              <p className="text-muted-foreground mb-3">
                Your data is stored securely using industry-standard encryption and security measures:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>All data is encrypted in transit and at rest</li>
                <li>Access controls and authentication protocols</li>
                <li>Regular security audits and monitoring</li>
                <li>Data is stored in secure cloud infrastructure</li>
                <li>Backup and disaster recovery procedures</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">5. Data Sharing and Disclosure</h2>
              <p className="text-muted-foreground mb-3">
                We do not sell, trade, or rent your personal information. We may share information only:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>With your explicit consent</li>
                <li>To comply with legal requirements</li>
                <li>With trusted service providers under strict agreements</li>
                <li>To protect our rights and prevent fraud</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">6. Your Rights (GDPR Compliance)</h2>
              <p className="text-muted-foreground mb-3">
                Under GDPR and other privacy laws, you have the following rights:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
                <li><strong>Restriction:</strong> Limit how we process your data</li>
                <li><strong>Objection:</strong> Object to certain types of processing</li>
              </ul>
              <p className="text-muted-foreground mt-3">
                You can exercise these rights through your account settings or by contacting us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">7. Cookies and Tracking</h2>
              <p className="text-muted-foreground">
                We use cookies and similar technologies to enhance your experience, analyze usage, and provide 
                personalized services. You can manage cookie preferences through our cookie banner or browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">8. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your information for as long as necessary to provide services and comply with legal obligations. 
                You can request data deletion at any time through your account settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">9. International Data Transfers</h2>
              <p className="text-muted-foreground">
                Your data may be processed in countries outside your residence. We ensure appropriate safeguards 
                are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">10. Updates to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any material changes 
                via email or through our platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">11. Contact Us</h2>
              <p className="text-muted-foreground">
                For any privacy-related questions, concerns, or to exercise your rights, please contact us:
              </p>
              <div className="mt-3 space-y-1 text-muted-foreground">
                <p><strong>Email:</strong> support@nextwaveai.space</p>
                <p><strong>Data Protection Officer:</strong> support@nextwaveai.space</p>
                <p><strong>Website:</strong> https://nextwaveai.space</p>
              </div>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;